import React, { useState, useEffect } from 'react';
import { Bell, AlertTriangle, X, ShoppingCart } from 'lucide-react';
import { getProducts, getOrders } from '../api';
import { useStructure } from '../contexts/StructureContext';
import { usePWA } from '../hooks/usePWA';

const StockAlerts = () => {
  const { currentStructure } = useStructure();
  const { updateBadge } = usePWA();
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentStructure) {
      fetchAlerts();
      const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [currentStructure]);

  useEffect(() => {
    const badgeCount = alerts.filter(a => !a.onOrder).length;
    updateBadge(badgeCount);
  }, [alerts, updateBadge]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const [productsRes, pendingOrdersRes, partialOrdersRes] = await Promise.all([
        getProducts(currentStructure.id),
        getOrders(currentStructure.id, 'pending').catch(() => ({ data: [] })),
        getOrders(currentStructure.id, 'partially_received').catch(() => ({ data: [] })),
      ]);
      const products = productsRes.data;

      // Build set of product IDs that are on active orders
      const onOrderProductIds = new Set();
      [...(pendingOrdersRes.data || []), ...(partialOrdersRes.data || [])].forEach(order => {
        (order.items || []).forEach(item => {
          if (item.productId) onOrderProductIds.add(item.productId);
        });
      });

      const lowStock = products.filter(p =>
        p.currentStock < p.idealStock && p.currentStock > 0 && p.idealStock > 0
      );

      const outOfStock = products.filter(p => p.currentStock === 0 && p.idealStock > 0);

      setAlerts([
        ...outOfStock.map(p => ({
          id: p.id,
          type: 'critical',
          product: p.name,
          message: 'Rupture de stock',
          onOrder: onOrderProductIds.has(p.id),
        })),
        ...lowStock.map(p => ({
          id: p.id,
          type: 'warning',
          product: p.name,
          message: `Stock bas (${p.currentStock}/${p.idealStock})`,
          onOrder: onOrderProductIds.has(p.id),
        }))
      ]);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const alertsNotOnOrder = alerts.filter(a => !a.onOrder);
  const alertsOnOrder = alerts.filter(a => a.onOrder);
  const criticalNotOnOrder = alertsNotOnOrder.filter(a => a.type === 'critical');
  const warningNotOnOrder = alertsNotOnOrder.filter(a => a.type === 'warning');
  const badgeCount = alertsNotOnOrder.length;

  if (!currentStructure) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setShowAlerts(!showAlerts)}
        className="relative p-2 hover:bg-primary-dark rounded transition"
        title="Alertes de stock"
      >
        <Bell size={24} />
        {badgeCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg animate-pulse">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </button>

      {showAlerts && (
        <>
          {/* Mobile overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40 md:hidden"
            onClick={() => setShowAlerts(false)}
          />

          {/* Dropdown */}
          <div className="fixed md:absolute right-4 md:right-0 top-16 md:top-12 w-[calc(100vw-2rem)] md:w-80 max-w-md bg-white rounded-lg shadow-xl border z-50">
            <div className="p-4 border-b flex justify-between items-center bg-gray-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Bell size={18} className="text-primary" />
                Alertes de stock
              </h3>
              <button
                onClick={() => setShowAlerts(false)}
                className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-200 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="max-h-[70vh] md:max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                Aucune alerte de stock
              </div>
            ) : (
              <>
                {criticalNotOnOrder.length > 0 && (
                  <div className="p-3 bg-red-50 border-b">
                    <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
                      <AlertTriangle size={16} />
                      Ruptures de stock ({criticalNotOnOrder.length})
                    </div>
                    {criticalNotOnOrder.map(alert => (
                      <div key={alert.id} className="text-sm text-red-700 py-1 pl-6">
                        {alert.product}
                      </div>
                    ))}
                  </div>
                )}

                {warningNotOnOrder.length > 0 && (
                  <div className="p-3 border-b">
                    <div className="flex items-center gap-2 text-orange-800 font-semibold mb-2">
                      <AlertTriangle size={16} />
                      Stock bas ({warningNotOnOrder.length})
                    </div>
                    {warningNotOnOrder.map(alert => (
                      <div key={alert.id} className="text-sm text-gray-700 py-1 pl-6">
                        {alert.product} - {alert.message}
                      </div>
                    ))}
                  </div>
                )}

                {alertsOnOrder.length > 0 && (
                  <div className="p-3 bg-blue-50 border-b">
                    <div className="flex items-center gap-2 text-blue-800 font-semibold mb-2">
                      <ShoppingCart size={16} />
                      En commande ({alertsOnOrder.length})
                    </div>
                    {alertsOnOrder.map(alert => (
                      <div key={alert.id} className="flex items-center gap-2 text-sm text-blue-700 py-1 pl-6">
                        <span>{alert.product}</span>
                        <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded font-medium">
                          En commande
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {badgeCount === 0 && alertsOnOrder.length > 0 && (
                  <div className="p-3 text-center text-sm text-green-700 bg-green-50">
                    Tous les produits en alerte sont en commande
                  </div>
                )}
              </>
            )}
          </div>

          {alerts.length > 0 && (
            <div className="p-3 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowAlerts(false);
                }}
                className="w-full bg-primary text-white px-4 py-2.5 rounded-lg hover:bg-primary-dark transition text-sm font-medium shadow-sm"
              >
                Voir la liste de courses
              </button>
            </div>
          )}
          </div>
        </>
      )}
    </div>
  );
};

export default StockAlerts;
